import { AUT_NIGHTLY_OPERATION_KEYS, ICO_BOX, ICO_FLASK, ICO_KEY, ICO_ROCKET, ICO_SHIELD } from './constants';
import { parseApiDate } from '../../../helpers/dates';

export function formatDuration(start, end) {
  if (!start) return '—';
  const ms = ((end ? parseApiDate(end) : new Date()) - parseApiDate(start));
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

export function commitsFromRun(run) {
  const b = run.result?.build_docker || {};
  return [
    { id: 'openmetadata',  sha: b.om_commit },
    { id: 'collate',       sha: b.collate_commit },
    { id: 'hybrid-runner', sha: b.hybrid_runner_commit },
    { id: 'ai-platform',   sha: b.caip_commit },
  ].filter(c => c.sha);
}

export function getStepIcon(name) {
  const k = (name || '').toLowerCase();
  // `construct_tests` is matched exactly: a bare `test` substring would also swallow `deploy_test`.
  if (k.startsWith('aut') || k.startsWith('omd') ||
      k.includes('unit_test') || k.includes('e2e') || k === 'construct_tests' ||
      k.includes('java_it') || k.includes('_it_') || k.includes('py_test')) return ICO_FLASK;
  if (k.startsWith('vulnerability') || k.includes('security_scan') ||
      k.includes('scan') || k.includes('security')) return ICO_SHIELD;
  if (k === 'sso_login' || k.includes('login')) return ICO_KEY;
  if (k === 'deploy_test' || k.startsWith('deploy') || k.startsWith('argocd')) return ICO_ROCKET;
  return ICO_BOX;
}

export function isVulnerabilityOperation(op) {
  const key = `${op?.operation || ''} ${op?.name || ''} ${op?.key || ''}`.toLowerCase();
  return key.includes('vulnerability_scan') || key.includes('vuln_scan');
}

export function isChangelogOperation(op) {
  const key = `${op?.operation || ''} ${op?.name || ''} ${op?.key || ''}`.toLowerCase();
  return key.includes('changelog');
}

export function vulnerabilityFindingsAreFinal(op) {
  return isVulnerabilityOperation(op) && ['completed', 'errored'].includes(op?.status);
}

// Mirrors is_nightly_status_excluded_step in backend controllers/nightly.py: every other step
// decides whether the nightly run succeeds, and so whether it is eligible for auto-rollout.
export function isNightlyRequiredStep(key) {
  return !!key && !(key.startsWith('vulnerability_scan_') || key.includes('security_scan') || key === 'changelog');
}

export function isAutNightlyOperation(op) {
  return AUT_NIGHTLY_OPERATION_KEYS.includes(op?.key);
}

export function operationResultUrl(op) {
  return op?.result?.url || op?.url || null;
}

export function operationCommitUrl(op) {
  return op?.result?.commit_url || null;
}

export function operationConclusion(op) {
  return op?.result?.conclusion || op?.conclusion || null;
}

// A step recorded with a skip reason and no dispatched operation: nothing to load or rerun.
export function isSkippedStep(op) {
  return Boolean(op?.skipped) && !op?.operation_id;
}

export function displayStatusForNightlyOperation(op) {
  // A skip marker is stored as completed; show it as skipped so it never reads as a pass.
  if (op?.skipped) {
    return 'skipped';
  }
  const outcome = op?.outcome || op?.result?.aut_outcome;
  if (
    op?.status === 'completed' &&
    AUT_NIGHTLY_OPERATION_KEYS.includes(op?.key) &&
    Number(outcome?.unexpected || 0) > 0
  ) {
    return 'errored';
  }
  return op?.status;
}

export function conclusionColor(conclusion) {
  const value = String(conclusion || '').toLowerCase();
  if (value === 'success') return 'var(--success-700)';
  if (['failure', 'failed', 'cancelled', 'timed_out'].includes(value)) return 'var(--error-700)';
  if (['skipped', 'neutral'].includes(value)) return 'var(--fg-muted)';
  return 'var(--warning-700)';
}

export function mergeOperationSummary(summary, loaded) {
  return {
    ...(summary || {}),
    ...(loaded || {}),
    result: loaded?.result || summary?.result,
  };
}
